import React from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: React.ReactNode;
  color?: "primary" | "success" | "warning" | "error" | "info";
  format?: "currency" | "percentage" | "number";
  size?: "small" | "medium" | "large";
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  changeLabel,
  icon,
  color = "primary",
  format = "number",
  size = "medium",
}) => {
  const theme = useTheme();

  const formatValue = (val: string | number): string => {
    if (typeof val === "string") return val;

    switch (format) {
      case "currency":
        return `${(val / 1000).toFixed(1)}K BHD`;
      case "percentage":
        return `${val.toFixed(1)}%`;
      default:
        return val.toLocaleString();
    }
  };

  const getChangeColor = (changeValue?: number) => {
    if (!changeValue) return "text.secondary";
    return changeValue > 0
      ? "success.main"
      : changeValue < 0
      ? "error.main"
      : "text.secondary";
  };

  const getChangeIcon = (changeValue?: number) => {
    if (!changeValue) return <RemoveIcon sx={{ fontSize: 16 }} />;
    return changeValue > 0 ? (
      <TrendingUpIcon sx={{ fontSize: 16 }} />
    ) : (
      <TrendingDownIcon sx={{ fontSize: 16 }} />
    );
  };

  const getColorValue = () => {
    switch (color) {
      case "success":
        return theme.palette.success.main;
      case "warning":
        return theme.palette.warning.main;
      case "error":
        return theme.palette.error.main;
      case "info":
        return theme.palette.info.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const cardHeight = size === "small" ? 120 : size === "large" ? 180 : 150;
  const titleSize =
    size === "small" ? "body2" : size === "large" ? "h6" : "subtitle2";
  const valueSize = size === "small" ? "h5" : size === "large" ? "h3" : "h4";

  const primaryColor = getColorValue();

  return (
    <Box
      sx={{
        height: cardHeight,
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 1px 3px rgba(0,0,0,0.3)"
            : "0 1px 3px rgba(0,0,0,0.1)",
        "&:hover": {
          borderColor: primaryColor,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 4px 12px rgba(0,0,0,0.4), 0 0 0 1px ${alpha(
                  primaryColor,
                  0.2
                )}`
              : `0 4px 12px rgba(0,0,0,0.15), 0 0 0 1px ${alpha(
                  primaryColor,
                  0.2
                )}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Top accent line */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: primaryColor,
          zIndex: 1,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          variant={titleSize}
          sx={{
            color: "text.secondary",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            lineHeight: 1.2,
            fontSize: { xs: "0.75rem", sm: "0.8125rem" },
          }}
        >
          {title}
        </Typography>
        {icon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: alpha(primaryColor, 0.1),
              color: primaryColor,
              "& svg": {
                fontSize: "1.25rem",
              },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>

      {/* Value */}
      <Box sx={{ position: "relative", zIndex: 1, my: 1 }}>
        <Typography
          variant={valueSize}
          sx={{
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1,
            fontSize: { xs: "1.75rem", sm: "2rem" },
          }}
        >
          {formatValue(value)}
        </Typography>
      </Box>

      {/* Change indicator */}
      {(change !== undefined || changeLabel) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            position: "relative",
            zIndex: 1,
          }}
        >
          {change !== undefined && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: alpha(
                  change > 0
                    ? theme.palette.success.main
                    : change < 0
                    ? theme.palette.error.main
                    : theme.palette.grey[500],
                  0.1
                ),
                border: `1px solid ${alpha(
                  change > 0
                    ? theme.palette.success.main
                    : change < 0
                    ? theme.palette.error.main
                    : theme.palette.grey[500],
                  0.2
                )}`,
              }}
            >
              <Box
                sx={{
                  color: getChangeColor(change),
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {getChangeIcon(change)}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: getChangeColor(change),
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              >
                {change > 0 ? "+" : ""}
                {change?.toFixed(1)}%
              </Typography>
            </Box>
          )}
          {changeLabel && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.6875rem",
                fontWeight: 400,
                ml: change !== undefined ? 0.5 : 0,
              }}
            >
              {changeLabel}
            </Typography>
          )}
        </Box>
      )}

      {/* Decorative Element */}
      <Box
        sx={{
          position: "absolute",
          bottom: -10,
          right: -10,
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: alpha(primaryColor, 0.05),
          zIndex: 0,
        }}
      />
    </Box>
  );
};
